/**
 * <advertorial-reviews> — the review carousel used by sections/advertorial-reviews.liquid.
 *
 * Swiper is already loaded site-wide from layout/theme.liquid, so this only wires
 * it up. All selectors are scoped to the element rather than the document, so
 * several carousels can coexist on one page without stealing each other's
 * pagination and arrows.
 *
 * Behaviour notes:
 * - `slidesPerView: 'auto'` with fixed-width slides, aligned to the start rather
 *   than centred: the first review has to sit flush with the container's left
 *   edge, fully visible. The peek therefore appears on the trailing edge only.
 * - `rewind` rather than `loop`, deliberately. Swiper implements looping by
 *   cloning slides into the DOM, and its a11y module does not hide the clones, so
 *   a screen reader would read every review two or three times over. `rewind`
 *   wraps from the last slide back to the first with no clones. The only thing
 *   lost is the left-hand peek while sitting on the very first slide.
 * - Autoplay and slide transitions are switched off under
 *   `prefers-reduced-motion`, and autoplay always stops for good once the visitor
 *   interacts with the carousel.
 */
(function () {
  if (customElements.get('advertorial-reviews')) return;

  const SWIPER_WAIT_MS = 5000;

  /** Resolves once window.Swiper is available, or rejects after SWIPER_WAIT_MS. */
  function whenSwiperReady() {
    if (window.Swiper) return Promise.resolve();

    return new Promise(function (resolve, reject) {
      const startedAt = Date.now();
      (function poll() {
        if (window.Swiper) return resolve();
        if (Date.now() - startedAt > SWIPER_WAIT_MS) {
          return reject(new Error('[advertorial-reviews] Swiper never became available.'));
        }
        window.requestAnimationFrame(poll);
      })();
    });
  }

  class AdvertorialReviews extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('.swiper');
      this.slideCount = this.querySelectorAll('.swiper-slide').length;
      if (!this.root || this.slideCount === 0) return;

      whenSwiperReady()
        .then(() => this.init())
        .catch((error) => {
          // Without Swiper the track stays a plain horizontally scrollable row,
          // so the reviews are still readable. Nothing to recover from.
          console.warn(error.message);
        });
    }

    disconnectedCallback() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    get autoplayDelay() {
      const delay = parseInt(this.dataset.autoplayDelay || '0', 10);
      return Number.isNaN(delay) ? 0 : delay;
    }

    init() {
      const reduced = this.prefersReducedMotion;
      const autoplayDelay = reduced ? 0 : this.autoplayDelay;
      const pagination = this.querySelector('.advertorial-reviews__pagination');
      const prevEl = this.querySelector('.advertorial-reviews__nav--prev');
      const nextEl = this.querySelector('.advertorial-reviews__nav--next');

      const options = {
        slidesPerView: 'auto',
        spaceBetween: 38, // Figma: 320px card on a 358px pitch
        rewind: true,
        watchOverflow: true,
        speed: reduced ? 0 : 500,
        grabCursor: true,
        keyboard: { enabled: true, onlyInViewport: true },
        a11y: {
          enabled: true,
          containerRoleDescriptionMessage: this.dataset.a11yCarousel || 'carousel',
          itemRoleDescriptionMessage: this.dataset.a11ySlide || 'slide',
          prevSlideMessage: this.dataset.a11yPrev || 'Previous slide',
          nextSlideMessage: this.dataset.a11yNext || 'Next slide',
          paginationBulletMessage: this.dataset.a11yBullet || 'Go to slide {{index}}'
        },
        autoplay: autoplayDelay > 0 ? { delay: autoplayDelay, disableOnInteraction: true, pauseOnMouseEnter: true } : false
      };

      if (pagination) {
        options.pagination = { el: pagination, clickable: true, bulletElement: 'button' };
        // Swiper builds the bullets without a `type`, which would default to
        // "submit" and post any enclosing form when a dot is clicked.
        options.on = {
          paginationRender(swiper, el) {
            el.querySelectorAll('button:not([type])').forEach(function (bullet) {
              bullet.type = 'button';
            });
          }
        };
      }

      // The arrows are optional in the section settings; only wire navigation up
      // when both buttons are actually in the DOM.
      if (prevEl && nextEl) {
        options.navigation = { prevEl: prevEl, nextEl: nextEl };
      }

      this.swiper = new window.Swiper(this.root, options);
    }
  }

  customElements.define('advertorial-reviews', AdvertorialReviews);
})();
